import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  tokens,
} from "@fluentui/react-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import { useGetUserAccessLevel } from "../hooks/useGetUserAccessLevel";
import { ReportType, useGetReportFile } from "../services/ReportService";

type ReportDefinition = {
  reportType: ReportType;
  titleKey: string;
};

const documentsWithLinksAccessLevel = 4;

const reports: ReportDefinition[] = [
  {
    reportType: ReportType.PublishedActivities,
    titleKey: "reportsPage.publishedActivities",
  },
  {
    reportType: ReportType.DraftActivities,
    titleKey: "reportsPage.draftActivities",
  },
  {
    reportType: ReportType.PublishedDocuments,
    titleKey: "reportsPage.publishedDocuments",
  },
  {
    reportType: ReportType.DraftDocuments,
    titleKey: "reportsPage.draftDocuments",
  },
  {
    reportType: ReportType.PublishedLinks,
    titleKey: "reportsPage.publishedLinks",
  },
  {
    reportType: ReportType.DraftLinks,
    titleKey: "reportsPage.draftLinks",
  },
  {
    reportType: ReportType.PublishedProcessImages,
    titleKey: "reportsPage.publishedActivityImages",
  },
  {
    reportType: ReportType.DraftProcessImages,
    titleKey: "reportsPage.draftActivityImages",
  },
  {
    reportType: ReportType.DocumentsWithLinks,
    titleKey: "documentLinksReport.title",
  },
];

const useClasses = makeStyles({
  root: {
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  title: {
    textAlign: "center",
    marginBottom: "20px",
  },
  sectionTitle: {
    marginTop: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalS,
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
  },
  reportNameCell: {
    overflowWrap: "anywhere",
  },
  actionsCell: {
    width: "200px",
    whiteSpace: "nowrap",
  },
});

type ReportDownloadRowProps = {
  report: ReportDefinition;
  flat?: boolean;
};

function ReportDownloadRow({ report, flat = false }: ReportDownloadRowProps) {
  const classes = useClasses();
  const { t } = useTranslation();
  const reportFile = useGetReportFile(report.reportType, flat);
  const [isDownloading, setIsDownloading] = useState(false);

  const isLoading = isDownloading || reportFile.isRefetching;

  const downloadReport = async () => {
    if (isLoading) {
      return;
    }

    setIsDownloading(true);

    try {
      const result = await reportFile.refetch();
      if (!result.data) return;

      const url = URL.createObjectURL(result.data.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.data.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      return;
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <TableRow>
      <TableCell className={classes.reportNameCell}>
        {t(report.titleKey)}
      </TableCell>
      <TableCell className={classes.actionsCell}>
        <ButtonWithLoading
          isLoading={isLoading}
          onClick={downloadReport}
          text={t("documentLinksReport.download")}
        />
      </TableCell>
    </TableRow>
  );
}

function ReportsPage() {
  const classes = useClasses();
  const { t } = useTranslation();
  const userAccessLevel = useGetUserAccessLevel();
  const visibleReports = reports.filter(
    (report) =>
      report.reportType !== ReportType.DocumentsWithLinks ||
      userAccessLevel >= documentsWithLinksAccessLevel
  );

  return (
    <div className={classes.root}>
      <h1 className={classes.title}>{t("reportsPage.title")}</h1>
      <Table aria-label={t("reportsPage.title")} className={classes.table}>
        <TableBody>
          {visibleReports.map((report) => (
            <ReportDownloadRow key={report.reportType} report={report} />
          ))}
        </TableBody>
      </Table>
      {userAccessLevel >= 4 && (
        <section aria-label={t("reportsPage.flatTitle")}>
          <h2 className={classes.sectionTitle}>{t("reportsPage.flatTitle")}</h2>
          <Table
            aria-label={t("reportsPage.flatTitle")}
            className={classes.table}
          >
            <TableBody>
              {reports.map((report) => (
                <ReportDownloadRow
                  key={report.reportType}
                  report={report}
                  flat
                />
              ))}
            </TableBody>
          </Table>
        </section>
      )}
    </div>
  );
}

export default ReportsPage;
